import { describeLicenseSettingChange } from '@components/Reporting/licenseSettingChange';
import ReportFigure from '@components/Reporting/ReportFigure';
import UsageSparkline from '@components/Reporting/UsageSparkline';
import Header from '@components/Shared/Layout/Header';
import { Loading, LoadFailed } from '@components/Shared/LoadState';
import { addUtcDays, utcDayOf } from '@constants/utcDay';
import {
  Alert, Box, Card, CardContent, Chip, MenuItem, Pagination, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {
  useGetActivationLogsQuery, useGetActivationLogSummaryQuery, useGetSeatUsageSeriesQuery,
  type ActivationLogEntry, type ActivationLogQuery, type ActivationOutcome
} from '@store/api/activationLogEndpoints';
import { useGetLicensesQuery } from '@store/api/licenseEndpoints';
import { Fragment, useMemo, useState } from 'react';

/**
 * "Last N days" is N UTC dates, today included, as on the Seat usage page and in the cloud's
 * dashboards: from the start of the UTC day N - 1 days ago until now. The last 24 hours stay a
 * rolling window.
 */
const RANGES: { value: string, label: string, hours?: number, days?: number }[] = [
  { value: '24h', label: 'Last 24 hours', hours: 24 },
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '365', label: 'Last 12 months', days: 365 },
];

const OUTCOMES: { value: ActivationOutcome | '', label: string }[] = [
  { value: '', label: 'All outcomes' },
  { value: 'seatGranted', label: 'Seat granted' },
  { value: 'seatReclaimed', label: 'Seat reclaimed' },
  { value: 'seatReleased', label: 'Seat released' },
  { value: 'seatRevoked', label: 'Seat revoked by admin' },
  { value: 'seatDenied', label: 'Seat denied' },
  { value: 'authFailed', label: 'Authentication failed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'ticketsReported', label: 'Offline use reported' },
  { value: 'ticketsBeyondCapacity', label: 'Offline use beyond the limit' },
  { value: 'licenseSettingsChanged', label: 'License settings changed by admin' },
];

/**
 * Colour carries the same meaning everywhere: red is a refusal, green is a grant, grey is a seat
 * ending on its own. A revocation is none of those. It is somebody stepping in, and on a screen
 * read to explain a dip in usage that is the row worth finding first. So is an administrator
 * changing how long seats are held or how long devices may work offline.
 */
const outcomeColour = (outcome: ActivationOutcome) => {
  if (outcome === 'seatGranted' || outcome === 'seatReclaimed') return 'success' as const;
  if (outcome === 'seatReleased') return 'default' as const;
  if (outcome === 'seatRevoked' || outcome === 'licenseSettingsChanged') return 'warning' as const;
  // Offline use is neither a grant nor a refusal: a device reporting what it did while it could
  // not reach the server. Beyond the limit is the one an administrator will want to find.
  if (outcome === 'ticketsReported') return 'info' as const;
  if (outcome === 'ticketsBeyondCapacity') return 'warning' as const;

  return 'error' as const;
};

/**
 * A package identifier that may wrap after each dot. `com.cdm.supar.composer` is one word to the
 * browser, and the widest in the table, so on its own it held the column at its full width.
 */
const breakAtDots = (identifier: string) => identifier.split('.').map((part, index) => (
  <Fragment key={index}>{index > 0 && <>.<wbr /></>}{part}</Fragment>
));

/**
 * The events table's cells, tighter at their sides from `md` up so the table fits its card on a
 * desktop screen. With every outcome it was 1130 px in a 1066 px card at 1440, and its last column
 * was cut until scrolled to; with these cells and an application's name free to wrap at its dots,
 * the same rows fit from 1280 up. Narrower, the table scrolls sideways inside its card, as every
 * table here does.
 */
const EVENTS_TABLE_SX = { '& .MuiTableCell-root': { px: { md: 1 } } };

/** What a ticket report filed, for the Reason column of an offline-use row. */
const ticketSummary = (tickets?: number, beyond?: number) => {
  if (tickets == null) return '-';

  const filed = tickets.formatCount('ticket', 'tickets');

  return beyond ? `${filed}, ${beyond} beyond the limit` : filed;
};

/**
 * An administrator's change to a license's settings, across the columns a device's event fills:
 * which license, who changed it, and each value from what to what.
 */
const SettingsChangeCells = ({ entry, licenseName }: { entry: ActivationLogEntry, licenseName: string }) => (
  <TableCell colSpan={6}>
    {/* No width of its own on a wide screen: the text wraps in the columns the other events size,
        rather than widening the table for a long license name. On a narrow one, where the table
        scrolls sideways, it wraps at the width of the screen, so scrolled to it, it reads whole. */}
    <Box sx={{ width: { xs: 'calc(100vw - 120px)', md: 0 }, minWidth: { md: '100%' } }}>
      <Typography variant='body2'>
        <strong>{licenseName}</strong>, changed by <strong>{entry.actor ?? 'an administrator'}</strong>
      </Typography>
      {(entry.changes ?? []).map(change => (
        <Typography key={change.field} variant='body2' color='text.secondary'>
          {describeLicenseSettingChange(change)}
        </Typography>
      ))}
    </Box>
  </TableCell>
);

const ActivationLogsPage = () => {
  const [range, setRange] = useState('7');
  const [licenseId, setLicenseId] = useState('');
  const [outcome, setOutcome] = useState<ActivationOutcome | ''>('');
  const [page, setPage] = useState(1);

  const { data: licenses = [] } = useGetLicensesQuery();

  // Recomputed only when the range changes, so paging or filtering does not silently slide
  // the window forward and renumber the rows underneath the operator.
  const period = useMemo(() => {
    const now = new Date();
    const { days, hours = 24 } = RANGES.find(item => item.value === range) ?? {};

    return {
      from: days
        ? `${addUtcDays(utcDayOf(now), -(days - 1))}T00:00:00.000Z`
        : new Date(now.getTime() - hours * 60 * 60 * 1000).toISOString(),
      to: now.toISOString()
    };
  }, [range]);

  const query: ActivationLogQuery = {
    ...period,
    licenseId: licenseId || undefined,
    outcome: outcome || undefined
  };

  // The answers for the filters on screen only (`currentData`), so that neither a filter still
  // loading nor one that failed shows the previous filter's figures or events under it.
  const {
    currentData: summary, isError: summaryFailed, refetch: refetchSummary
  } = useGetActivationLogSummaryQuery(query);
  const {
    currentData: series, isError: seriesFailed, refetch: refetchSeries
  } = useGetSeatUsageSeriesQuery(query);
  const {
    currentData: logs, isError: logsFailed, refetch: refetchLogs
  } = useGetActivationLogsQuery({ ...query, page, limit: 50 });

  const entitled = summary ? summary.entitled.supar + summary.entitled.composer : 0;
  const peak = summary?.peakConcurrent;
  const current = summary?.currentInUse;

  /** As the filter names a license, or by what the event recorded when it is no longer installed. */
  const licenseName = (entry: ActivationLogEntry) => {
    const license = licenses.find(item => item.licenseId === entry.licenseId);

    return license?.label || license?.id || entry.activationId || entry.licenseId?.slice(0, 8) || 'a license';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Activity'
        description='Seat usage over time and the events behind it.'
      />

      <Alert severity='info' sx={{ mb: 2 }}>
        Events are recorded without device names or IP addresses. A device appears as a hash
        that stays the same for one calendar month and changes the next, so an incident can be
        followed without building a history of anybody. A change to a license's offline credit
        or seat time is recorded with the user name of the administrator who made it.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* A gap rather than margins, so a wrapped second row starts where the first did. */}
          <Stack direction='row' spacing={2} useFlexGap sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
            <TextField
              select size='small' label='Range' value={range} sx={{ minWidth: 170 }}
              onChange={event => { setRange(event.target.value); setPage(1); }}
            >
              {RANGES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>

            <TextField
              select size='small' label='License' value={licenseId} sx={{ minWidth: 200 }}
              // "All licenses" and "All outcomes" are choices, so they show as the value.
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              onChange={event => { setLicenseId(event.target.value); setPage(1); }}
            >
              <MenuItem value=''>All licenses</MenuItem>
              {licenses.map(license => (
                <MenuItem key={license.licenseId} value={license.licenseId}>
                  {license.label || license.id || license.licenseId.slice(0, 8)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select size='small' label='Outcome' value={outcome} sx={{ minWidth: 190 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              onChange={event => { setOutcome(event.target.value as ActivationOutcome | ''); setPage(1); }}
            >
              {OUTCOMES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
          </Stack>

          {/* No figures until they are known: not while they load, and not under a failure, where a
              dash or a zero would read as nobody having used anything. */}
          {summaryFailed || seriesFailed ? (
            <LoadFailed
              text='The usage figures could not be loaded.'
              onRetry={() => { void refetchSummary(); void refetchSeries(); }}
            />
          ) : !summary || !series ? (
            <Loading text='Loading the usage figures…' />
          ) : (
            <>
              <Stack direction='row' spacing={4} useFlexGap sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
                <ReportFigure
                  label='Peak concurrent'
                  value={peak ? String(peak.supar + peak.composer) : '-'}
                  hint={peak ? `SuPAR ${peak.supar} · Composer ${peak.composer}` : 'no samples in this window'}
                />
                <ReportFigure
                  label='In use now'
                  value={current ? String(current.supar + current.composer) : '-'}
                  hint={current ? `SuPAR ${current.supar} · Composer ${current.composer}` : undefined}
                />
                <ReportFigure
                  label='Entitled'
                  value={String(entitled)}
                  hint={`SuPAR ${summary.entitled.supar} · Composer ${summary.entitled.composer}`}
                />
                <ReportFigure
                  label='Average hold'
                  value={summary.averageHeldSeconds?.formatDuration() ?? '-'}
                  hint={summary.closedSessions.formatCount('completed session', 'completed sessions')}
                />
                <ReportFigure
                  label='Offline use reported'
                  value={summary.offlineUsage.tickets ? summary.offlineUsage.seconds.formatDuration() : '-'}
                  hint={summary.offlineUsage.tickets
                    ? `${summary.offlineUsage.ticketsBeyondCapacity} of ` +
                      `${summary.offlineUsage.tickets.formatCount('ticket', 'tickets')} beyond the limit`
                    : 'none reported in this window'}
                />
              </Stack>

              <UsageSparkline points={series} limit={entitled} />

              {Object.keys(summary.refusalsByReason).length > 0 && (
                <Stack direction='row' spacing={1} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 1 }}>
                  {Object.entries(summary.refusalsByReason).map(([reason, count]) => (
                    <Chip key={reason} size='small' color='error' variant='outlined' label={`${reason} ${count}`} />
                  ))}
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>Events</Typography>

          {/* "Nothing recorded" only once the answer says so: not while it loads, and not when it failed. */}
          {logsFailed ? (
            <LoadFailed text='The events could not be loaded.' onRetry={() => { void refetchLogs(); }} />
          ) : !logs ? (
            <Loading text='Loading the events…' />
          ) : !logs.items.length ? (
            <Typography variant='body2' color='text.secondary'>Nothing recorded in this window.</Typography>
          ) : (
            <>
              {/* Scrolls sideways on a narrow screen rather than cutting off the columns on the right. */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small' sx={EVENTS_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Application</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>Held / used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.items.map((entry, index) => (
                      <TableRow key={`${entry.createdAt}-${index}`}>
                        <TableCell>{new Date(entry.createdAt).toFormattedDateTimeString()}</TableCell>
                        <TableCell>
                          <Chip size='small' variant='outlined' color={outcomeColour(entry.outcome)} label={entry.outcome} />
                        </TableCell>
                        {entry.outcome === 'licenseSettingsChanged' ? (
                          <SettingsChangeCells entry={entry} licenseName={licenseName(entry)} />
                        ) : (
                          <>
                            <TableCell>{entry.failureReason ?? ticketSummary(entry.ticketCount, entry.beyondCapacityCount)}</TableCell>
                            <TableCell>{entry.packageIdentifier ? breakAtDots(entry.packageIdentifier) : '-'}</TableCell>
                            <TableCell><code>{entry.deviceHash ?? '-'}</code></TableCell>
                            <TableCell>{entry.deviceModel ?? '-'}</TableCell>
                            <TableCell>{entry.version ?? '-'}</TableCell>
                            <TableCell align='right'>{(entry.heldSeconds ?? entry.ticketSeconds)?.formatDuration() ?? '-'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mt: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  {logs.total.formatCount('event', 'events')}
                </Typography>
                <Pagination
                  size='small'
                  count={Math.max(1, Math.ceil(logs.total / logs.limit))}
                  page={logs.page}
                  onChange={(_event, value) => setPage(value)}
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivationLogsPage;
